import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AppDialog,
  Button,
  MiniLoader,
  Select,
  StatusPill,
  TextInput,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import type { TimelineElement } from '../../context/TimelineContext'
import {
  listEduPtmSlots,
  cancelEduPtmBooking,
  joinEduPtmMeeting,
  mapEduPtmError,
  type EduPtmDayItem,
  type EduPtmSlotItem,
  type EduPtmBookingStatus,
} from '../../services/eduPtmService'
import { utcIsoToZonedParts, DEFAULT_EDU_TIMEZONE } from '../../utils/eduZonedTime'
import { getTeachersForPtmDayAudience } from './eduPtmHelpers'
import { EduPtmBookOnBehalfDialog } from './EduPtmBookOnBehalfDialog'
import { EduPtmRescheduleDialog } from './EduPtmRescheduleDialog'
import { EduPtmOutcomeNotesDialog } from './EduPtmOutcomeNotesDialog'
import './eduPtm.css'

export interface EduPtmSlotBoardDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  day: EduPtmDayItem
  isAdmin: boolean
  currentUserId: string
  elements: TimelineElement[]
  isDemo?: boolean
  timezone?: string
  initialTeacherUid?: string
  directoryMembers?: Array<{ uid: string; displayName?: string; email?: string }>
}

export const EduPtmSlotBoardDialog = ({
  isOpen,
  onClose,
  workspaceId,
  day,
  isAdmin,
  currentUserId,
  elements,
  isDemo = false,
  timezone,
  initialTeacherUid,
  directoryMembers,
}: EduPtmSlotBoardDialogProps) => {
  const { t } = useNls()
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])
  const effectiveTz = timezone || DEFAULT_EDU_TIMEZONE

  // Assigned teachers
  const assignedTeachers = useMemo(() => {
    const list = getTeachersForPtmDayAudience(day.audience, elements, directoryMembers)
    if (list.length === 0) {
      return [{ uid: currentUserId, name: currentUserId }]
    }
    return list
  }, [day.audience, elements, directoryMembers, currentUserId])

  const [selectedTeacherUid, setSelectedTeacherUid] = useState<string>(() => {
    if (initialTeacherUid) return initialTeacherUid
    if (!isAdmin && !isDemo) return currentUserId
    return assignedTeachers[0]?.uid || currentUserId
  })

  const [slots, setSlots] = useState<EduPtmSlotItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // Sub-dialog states
  const [bookSlotStart, setBookSlotStart] = useState<string | null>(null)
  const [rescheduleBooking, setRescheduleBooking] = useState<{ id: string; learnerName: string } | null>(null)
  const [outcomeBooking, setOutcomeBooking] = useState<{
    id: string
    learnerName: string
    status: EduPtmBookingStatus
  } | null>(null)

  // Cancel Booking Modal State
  const [cancelBookingId, setCancelBookingId] = useState<string | null>(null)
  const [cancelReason, setCancelReason] = useState<string>('')
  const [cancelModalError, setCancelModalError] = useState<string | null>(null)
  const [isCancellingBooking, setIsCancellingBooking] = useState(false)

  // Online Joining State
  const [joiningBookingId, setJoiningBookingId] = useState<string | null>(null)

  const formatBookedBy = (bookedByUid?: string) => {
    if (!bookedByUid) return t('NLSEduPtmFamilyLabel')
    const staffMember = directoryMembers?.find((m) => m.uid === bookedByUid)
    if (staffMember) {
      return staffMember.displayName || staffMember.email || staffMember.uid
    }
    return t('NLSEduPtmFamilyLabel')
  }

  const fetchSlots = useCallback(async () => {
    if (!isOpen || !selectedTeacherUid) return
    try {
      setIsLoading(true)
      setErrorMsg(null)
      const res = await listEduPtmSlots({
        workspaceId,
        dayId: day.id,
        teacherUid: selectedTeacherUid,
      })
      setSlots(res.slots || [])
    } catch (err: unknown) {
      setErrorMsg(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsLoading(false)
    }
  }, [isOpen, selectedTeacherUid, workspaceId, day.id])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on slot refresh
    void fetchSlots()
  }, [fetchSlots, refreshKey])

  const handleJoinMeeting = async (bookingId: string) => {
    try {
      setJoiningBookingId(bookingId)
      setErrorMsg(null)
      const res = await joinEduPtmMeeting({ workspaceId, bookingId })
      if (res?.joinUrl) {
        window.open(res.joinUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err: unknown) {
      setErrorMsg(tRef.current(mapEduPtmError(err)))
    } finally {
      setJoiningBookingId(null)
    }
  }

  const handleCancelBooking = async () => {
    if (!cancelBookingId) return
    try {
      setIsCancellingBooking(true)
      setCancelModalError(null)
      await cancelEduPtmBooking({
        workspaceId,
        bookingId: cancelBookingId,
        reason: cancelReason.trim() || undefined,
      })
      setCancelBookingId(null)
      setCancelReason('')
      setRefreshKey((k) => k + 1)
    } catch (err: unknown) {
      setCancelModalError(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsCancellingBooking(false)
    }
  }

  const formatSlotTime = (startIso: string, endIso: string) => {
    const sParts = utcIsoToZonedParts(startIso, effectiveTz)
    const eParts = utcIsoToZonedParts(endIso, effectiveTz)
    return `${sParts.time} – ${eParts.time}`
  }

  return (
    <>
      <AppDialog
        open={
          isOpen &&
          !bookSlotStart &&
          !rescheduleBooking &&
          !outcomeBooking &&
          !cancelBookingId
        }
        onDismiss={onClose}
        title={`${t('NLSEduPtmSlotBoardTitle')}: ${day.title}`}
        size="lg"
        actions={[
          {
            label: t('NLSCommonActionClose'),
            onClick: onClose,
            variant: 'secondary',
          },
        ]}
      >
        <div className="eduPtmSlotBoard">
          {errorMsg && (
            <div className="eduDialog__banner eduDialog__banner--error" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Header Controls */}
          <div className="eduPtmSlotBoard__header">
            <div className="eduPtmCard__badges">
              <StatusPill
                tone={day.status === 'ACTIVE' ? 'success' : 'neutral'}
              >
                {day.status === 'ACTIVE' ? t('NLSEduPtmDayStatusActive') : t('NLSEduPtmDayStatusCancelled')}
              </StatusPill>
              <StatusPill tone="info">
                {day.meetingType === 'ONLINE' ? t('NLSEduPtmMeetingTypeOnline') : t('NLSEduPtmMeetingTypeInPerson')}
              </StatusPill>
            </div>

            {(isAdmin || isDemo) && assignedTeachers.length > 1 && (
              <div className="eduPtmSection__filters">
                <span className="eduPtmEditor__label">{t('NLSEduPtmTeacherPickerLabel')}:</span>
                <Select<string>
                  value={selectedTeacherUid}
                  onChange={(val) => setSelectedTeacherUid(val)}
                  options={assignedTeachers.map((tch) => ({
                    value: tch.uid,
                    label: tch.name,
                  }))}
                  ariaLabel={t('NLSEduPtmTeacherPickerLabel')}
                />
              </div>
            )}
          </div>

          {isLoading ? (
            <MiniLoader />
          ) : slots.length === 0 ? (
            <div className="eduPtmSection__empty">
              {t('NLSEduPtmEmptySlots')}
            </div>
          ) : (
            <div className="eduPtmSlotBoard__grid">
              {slots.map((slot, idx) => {
                if (slot.isBreak) {
                  return (
                    <div key={idx} className="eduPtmSlotCard eduPtmSlotCard--break">
                      <div className="eduPtmSlotCard__top">
                        <span className="eduPtmSlotCard__time">
                          {formatSlotTime(slot.slotStart, slot.slotEnd)}
                        </span>
                        <StatusPill tone="warning">{t('NLSEduPtmBreakLabel')}</StatusPill>
                      </div>
                      <span className="eduPtmEditor__help">{slot.breakLabel || t('NLSEduPtmBreakLabel')}</span>
                    </div>
                  )
                }

                const booking = slot.booking
                const isBooked = booking && booking.status === 'BOOKED'
                const isCancelled = booking && booking.status === 'CANCELLED'
                const isDone = booking && (booking.status === 'MET' || booking.status === 'NO_SHOW')
                const isFree = !booking || isCancelled

                return (
                  <div
                    key={idx}
                    className={`eduPtmSlotCard ${
                      isFree
                        ? 'eduPtmSlotCard--free'
                        : isDone
                          ? 'eduPtmSlotCard--done'
                          : isBooked
                            ? 'eduPtmSlotCard--booked'
                            : 'eduPtmSlotCard--cancelled'
                    }`}
                  >
                    <div className="eduPtmSlotCard__top">
                      <span className="eduPtmSlotCard__time">
                        {formatSlotTime(slot.slotStart, slot.slotEnd)}
                      </span>
                      <StatusPill
                        tone={
                          isFree
                            ? 'success'
                            : isDone
                              ? (booking?.status === 'MET' ? 'success' : 'danger')
                              : isBooked
                                ? 'info'
                                : 'neutral'
                        }
                      >
                        {isFree
                          ? t('NLSEduPtmSlotStatusFree')
                          : booking?.status === 'MET'
                            ? t('NLSEduPtmSlotStatusMet')
                            : booking?.status === 'NO_SHOW'
                              ? t('NLSEduPtmSlotStatusNoShow')
                              : booking?.status === 'CANCELLED'
                                ? t('NLSEduPtmSlotStatusCancelled')
                                : t('NLSEduPtmSlotStatusBooked')}
                      </StatusPill>
                    </div>

                    {booking && (
                      <div>
                        <div className="eduPtmSlotCard__learner">{booking.learnerName}</div>
                        <div className="eduPtmSlotCard__bookedBy">
                          {t('NLSEduPtmBookedByLabel')} {formatBookedBy(booking.bookedBy)}
                        </div>
                      </div>
                    )}

                    {!isDemo && day.status === 'ACTIVE' && (
                      <div className="eduPtmSlotCard__actions">
                        {isFree && (
                          <Button
                            variant="secondary"
                            onClick={() => setBookSlotStart(slot.slotStart)}
                          >
                            {t('NLSEduPtmBookOnBehalfButton')}
                          </Button>
                        )}

                        {isBooked && (
                          <>
                            <Button
                              variant="secondary"
                              onClick={() =>
                                setRescheduleBooking({
                                  id: booking.id,
                                  learnerName: booking.learnerName,
                                })
                              }
                            >
                              {t('NLSEduPtmRescheduleButton')}
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => {
                                setCancelBookingId(booking.id)
                                setCancelModalError(null)
                                setCancelReason('')
                              }}
                            >
                              {t('NLSEduPtmCancelBookingButton')}
                            </Button>
                          </>
                        )}

                        {(isBooked || isDone) && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setOutcomeBooking({
                                id: booking.id,
                                learnerName: booking.learnerName,
                                status: booking.status,
                              })
                            }
                          >
                            {t('NLSEduPtmOutcomeNotesButton')}
                          </Button>
                        )}

                        {day.meetingType === 'ONLINE' && booking && (isBooked || isDone) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Button
                              variant="primary"
                              onClick={() => handleJoinMeeting(booking.id)}
                              disabled={joiningBookingId === booking.id}
                            >
                              {t('NLSEduPtmJoinMeetingButton')}
                            </Button>
                            <span className="eduPtmStaffOnlyBadge">
                              {t('NLSEduPtmMeetingBadge')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </AppDialog>

      {/* Book on Behalf Dialog */}
      {bookSlotStart && (
        <EduPtmBookOnBehalfDialog
          isOpen={Boolean(bookSlotStart)}
          onClose={() => setBookSlotStart(null)}
          workspaceId={workspaceId}
          day={day}
          teacherUid={selectedTeacherUid}
          slotStart={bookSlotStart}
          elements={elements}
          isAdmin={isAdmin}
          currentUserId={currentUserId}
          onBooked={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* Reschedule Dialog */}
      {rescheduleBooking && (
        <EduPtmRescheduleDialog
          isOpen={Boolean(rescheduleBooking)}
          onClose={() => setRescheduleBooking(null)}
          workspaceId={workspaceId}
          bookingId={rescheduleBooking.id}
          learnerName={rescheduleBooking.learnerName}
          slots={slots}
          timezone={effectiveTz}
          onRescheduled={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* Outcome & Notes Dialog */}
      {outcomeBooking && (
        <EduPtmOutcomeNotesDialog
          isOpen={Boolean(outcomeBooking)}
          onClose={() => setOutcomeBooking(null)}
          workspaceId={workspaceId}
          bookingId={outcomeBooking.id}
          learnerName={outcomeBooking.learnerName}
          currentStatus={outcomeBooking.status}
          onUpdated={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* Cancel Booking Confirmation Modal */}
      {cancelBookingId && (
        <AppDialog
          open={Boolean(cancelBookingId)}
          onDismiss={() => {
            setCancelBookingId(null)
            setCancelModalError(null)
          }}
          title={t('NLSEduPtmCancelBookingTitle')}
          size="sm"
          actions={[
            {
              label: t('NLSEduPtmKeepBookingButton'),
              onClick: () => {
                setCancelBookingId(null)
                setCancelModalError(null)
              },
              variant: 'secondary',
              disabled: isCancellingBooking,
            },
            {
              label: t('NLSEduPtmCancelBookingButton'),
              onClick: handleCancelBooking,
              variant: 'danger',
              disabled: isCancellingBooking,
            },
          ]}
        >
          <div className="eduPtmEditor">
            {cancelModalError && (
              <div className="eduDialog__banner eduDialog__banner--error" role="alert">
                {cancelModalError}
              </div>
            )}
            <div className="eduPtmEditor__field">
              <span className="eduPtmEditor__label">{t('NLSEduPtmCancelBookingReasonLabel')}</span>
              <TextInput
                value={cancelReason}
                onChange={(val) => setCancelReason(val)}
                disabled={isCancellingBooking}
                aria-label={t('NLSEduPtmCancelBookingReasonLabel')}
              />
            </div>
          </div>
        </AppDialog>
      )}
    </>
  )
}
